import React, { useEffect, useState } from "react";
import styles from "./AdminCommunications.module.scss";
import {
  AdminDataService,
  CommunicationTemplate,
  AutomatedSequence,
} from "./mock-data/admin-data.service";
import Button from "../../components/Button/Button";
import { useHistory } from "react-router-dom";
import { UIContext } from "../../shared/shared-with-mobile/providers/ui.provider";
import SendLogTable from "./admin-components/SendLogTable";
import MockBadge from "../../components/MockBadge/MockBadge";

const AdminCommunications: React.FC = () => {
  const history = useHistory();
  const { dispatchToast } = React.useContext(UIContext);
  const [activeTab, setActiveTab] = useState<
    "templates" | "sequences" | "history"
  >("templates");
  const [emailTemplates, setEmailTemplates] = useState<CommunicationTemplate[]>(
    []
  );
  const [smsTemplates, setSmsTemplates] = useState<CommunicationTemplate[]>([]);
  const [sequences, setSequences] = useState<AutomatedSequence[]>([]);
  const [expandedSequence, setExpandedSequence] = useState<string | null>(null);
  const [
    previewTemplate,
    setPreviewTemplate,
  ] = useState<CommunicationTemplate | null>(null);

  useEffect(() => {
    setEmailTemplates(AdminDataService.getEmailTemplates());
    setSmsTemplates(AdminDataService.getSmsTemplates());
    setSequences(AdminDataService.getSequences());
  }, []);

  const categoryClass = (cat: string) => {
    switch (cat) {
      case "onboarding":
        return styles.catOnboarding;
      case "retention":
        return styles.catRetention;
      case "sales":
        return styles.catSales;
      default:
        return styles.catGeneral;
    }
  };

  const statusClass = (status: string) => {
    switch (status) {
      case "active":
        return styles.seqActive;
      case "paused":
        return styles.seqPaused;
      default:
        return styles.seqDraft;
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Communications</h1>
          <p className={styles.subtitle}>
            Templates, automated sequences, and send history
          </p>
        </div>
        <div className={styles.headerActions}>
          <Button
            theme="secondary"
            size="small"
            onClick={() => history.push("/admin/communications/bulk")}
          >
            Bulk Send
          </Button>
          <Button
            theme="primary"
            size="small"
            onClick={() =>
              dispatchToast({
                type: "info",
                message: "Template creator coming soon",
              })
            }
          >
            + Create Template
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${
            activeTab === "templates" ? styles.tabActive : ""
          }`}
          onClick={() => setActiveTab("templates")}
        >
          Templates
        </button>
        <button
          className={`${styles.tab} ${
            activeTab === "sequences" ? styles.tabActive : ""
          }`}
          onClick={() => setActiveTab("sequences")}
        >
          Sequences
        </button>
        <button
          className={`${styles.tab} ${
            activeTab === "history" ? styles.tabActive : ""
          }`}
          onClick={() => setActiveTab("history")}
        >
          Send Log
        </button>
      </div>

      {/* Templates Tab */}
      {activeTab === "templates" && (
        <div>
          <h3 className={styles.sectionTitle}>
            Email Templates <MockBadge />
          </h3>
          <div className={styles.templateGrid}>
            {emailTemplates.map((t) => (
              <div
                key={t.id}
                className={styles.templateCard}
                onClick={() => setPreviewTemplate(t)}
              >
                <div className={styles.templateName}>{t.name}</div>
                <div className={styles.templateSubject}>{t.subject}</div>
                <div className={styles.templateMeta}>
                  <span
                    className={`${styles.templateCategory} ${categoryClass(
                      t.category
                    )}`}
                  >
                    {t.category}
                  </span>
                  <span>Edited {t.lastEdited}</span>
                </div>
              </div>
            ))}
          </div>

          <h3 className={styles.sectionTitle} style={{ marginTop: 24 }}>
            SMS Templates <MockBadge />
          </h3>
          <div className={styles.templateGrid}>
            {smsTemplates.map((t) => (
              <div
                key={t.id}
                className={styles.templateCard}
                onClick={() => setPreviewTemplate(t)}
              >
                <div className={styles.templateName}>{t.name}</div>
                <div className={styles.templateSubject}>
                  {t.body.slice(0, 60)}...
                </div>
                <div className={styles.templateMeta}>
                  <span
                    className={`${styles.templateCategory} ${categoryClass(
                      t.category
                    )}`}
                  >
                    {t.category}
                  </span>
                  <span>{t.charCount} chars</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sequences Tab */}
      {activeTab === "sequences" && (
        <div>
          <div className={styles.sequenceHeader}>
            <h3 className={styles.sectionTitle}>
              Automated Sequences <MockBadge />
            </h3>
            <Button
              theme="primary"
              size="small"
              onClick={() =>
                dispatchToast({
                  type: "info",
                  message: "Sequence builder coming soon",
                })
              }
            >
              + Create Sequence
            </Button>
          </div>
          {sequences.map((seq) => (
            <div key={seq.id} className={styles.sequenceCard}>
              <div
                className={styles.sequenceCardHeader}
                onClick={() =>
                  setExpandedSequence(
                    expandedSequence === seq.id ? null : seq.id
                  )
                }
              >
                <div>
                  <div className={styles.sequenceNameRow}>
                    <strong>{seq.name}</strong>
                    <span
                      className={`${styles.seqStatus} ${statusClass(
                        seq.status
                      )}`}
                    >
                      {seq.status.charAt(0).toUpperCase() + seq.status.slice(1)}
                    </span>
                  </div>
                  <div className={styles.seqMeta}>
                    <span>Trigger: {seq.trigger}</span>
                    <span>{seq.steps.length} steps</span>
                    <span>{seq.enrolled} enrolled</span>
                    {seq.openRate !== null && (
                      <span
                        style={{
                          color:
                            seq.openRate >= 60
                              ? "#4CAF50"
                              : seq.openRate >= 40
                              ? "#FFC107"
                              : "#F44336",
                        }}
                      >
                        {seq.openRate}% open rate
                      </span>
                    )}
                  </div>
                </div>
                <span className={styles.expandArrow}>
                  {expandedSequence === seq.id ? "▲" : "▼"}
                </span>
              </div>
              {expandedSequence === seq.id && (
                <div className={styles.sequenceSteps}>
                  {seq.steps.map((step, i) => (
                    <div key={i} className={styles.stepRow}>
                      <div className={styles.stepConnector}>
                        <div
                          className={styles.stepDot}
                          style={{
                            background:
                              seq.status === "active"
                                ? "#ffbc17"
                                : seq.status === "paused"
                                ? "#FFC107"
                                : "#ababb7",
                          }}
                        />
                        {i < seq.steps.length - 1 && (
                          <div className={styles.stepLine} />
                        )}
                      </div>
                      <div className={styles.stepContent}>
                        <strong>{step.day}</strong> —{" "}
                        {step.channel === "sms" ? "SMS" : "Email"}:{" "}
                        {step.templateName}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Send Log Tab */}
      {activeTab === "history" && (
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Send Log</h3>
          <p
            style={{
              fontSize: 13,
              color: "#ababb7",
              marginTop: -8,
              marginBottom: 12,
            }}
          >
            Every email and SMS sent through the admin Communications panel,
            with delivery + engagement events from SendGrid + Twilio webhooks.
          </p>
          <SendLogTable />
        </div>
      )}

      {/* Template Preview Modal */}
      {previewTemplate && (
        <div
          className={styles.modalOverlay}
          onClick={() => setPreviewTemplate(null)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{previewTemplate.name}</h3>
              <button
                className={styles.modalClose}
                onClick={() => setPreviewTemplate(null)}
              >
                &times;
              </button>
            </div>
            {previewTemplate.subject && (
              <div className={styles.modalField}>
                <div className={styles.modalFieldLabel}>Subject</div>
                <div className={styles.modalFieldValue}>
                  {previewTemplate.subject}
                </div>
              </div>
            )}
            <div className={styles.modalField}>
              <div className={styles.modalFieldLabel}>Body Preview</div>
              <div className={styles.previewBody}>
                {previewTemplate.body.split(/(\{\{[^}]+\}\})/).map((part, i) =>
                  part.startsWith("{{") ? (
                    <span key={i} className={styles.token}>
                      {part}
                    </span>
                  ) : (
                    <span key={i}>{part}</span>
                  )
                )}
              </div>
            </div>
            <div className={styles.modalActions}>
              <Button
                theme="secondary"
                size="small"
                onClick={() => setPreviewTemplate(null)}
              >
                Close
              </Button>
              <Button
                theme="primary"
                size="small"
                onClick={() => {
                  setPreviewTemplate(null);
                  dispatchToast({
                    type: "info",
                    message: "Template editor coming soon",
                  });
                }}
              >
                Edit Template
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCommunications;
